import { Eye, EyeOff, ChevronDown, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

import type { ApiEntity } from "@/lib/api";
import { colorForEntity } from "@/lib/entity-color";

/**
 * MapLegend — dynamic, interactive panel showing what's on the map.
 *
 * Sections:
 * 1. **Layers**: master toggles for Markers / Areas / Routes / Labels / Heatmap
 * 2. **Colors**: one row per unique entity color, grouped by what's mapped
 *    under that color. Toggling a color hides all entities of that color from
 *    every map layer.
 * 3. **Signal types** (only when a signal list is passed): per-type toggle
 *    that filters the heatmap and any signal-based layers. Kept optional so
 *    routes that don't have signals loaded don't pay for the section.
 *
 * Reactive: groups recompute whenever the entity list changes (chat creates
 * a new corridor → new color appears in the legend automatically).
 */

export type LayerKey = "markers" | "areas" | "routes" | "labels" | "heatmap";

export interface LegendState {
  hiddenLayers: Set<LayerKey>;
  hiddenColors: Set<string>;
  hiddenSignalTypes: Set<string>;
}

interface ColorGroup {
  color: string;
  entities: ApiEntity[];
  // What geometry this group contributes — informs the swatch icon.
  hasMarker: boolean;
  hasArea: boolean;
  hasRoute: boolean;
}

function groupByColor(entities: ApiEntity[]): ColorGroup[] {
  const map = new Map<string, ColorGroup>();
  for (const e of entities) {
    const color = colorForEntity(e);
    const grp =
      map.get(color) ??
      ({ color, entities: [], hasMarker: false, hasArea: false, hasRoute: false } as ColorGroup);
    grp.entities.push(e);
    const hasCoord = (e.geolocation?.lat ?? e.lat) != null;
    if (hasCoord) grp.hasMarker = true;
    const oa = e.operational_area;
    if (oa) {
      if (oa.type === "Polygon" || oa.type === "MultiPolygon") grp.hasArea = true;
      if (oa.type === "LineString" || oa.type === "MultiLineString") grp.hasRoute = true;
    }
    map.set(color, grp);
  }
  // Sort: groups with more entities first, then alphabetically by primary name
  return [...map.values()].sort(
    (a, b) =>
      b.entities.length - a.entities.length || a.entities[0].name.localeCompare(b.entities[0].name),
  );
}

const LAYER_LABELS: Record<LayerKey, string> = {
  markers: "Markers",
  areas: "Areas",
  routes: "Routes",
  labels: "Labels",
  heatmap: "Signal heatmap",
};

export function MapLegend({
  entities,
  signalTypes,
  state,
  onChange,
}: {
  entities: ApiEntity[];
  /** Optional — pass the set of signal types present on the current map
   *  (parent already has the signals list, so we take a summary instead of
   *  the raw payload to keep this component's inputs small). */
  signalTypes?: { type: string; count: number }[];
  state: LegendState;
  onChange: (next: LegendState) => void;
}) {
  const groups = useMemo(() => groupByColor(entities), [entities]);
  const [collapsed, setCollapsed] = useState(false);

  const toggleLayer = (k: LayerKey) => {
    const next = new Set(state.hiddenLayers);
    next.has(k) ? next.delete(k) : next.add(k);
    onChange({ ...state, hiddenLayers: next });
  };
  const toggleColor = (c: string) => {
    const next = new Set(state.hiddenColors);
    next.has(c) ? next.delete(c) : next.add(c);
    onChange({ ...state, hiddenColors: next });
  };
  const toggleSignalType = (t: string) => {
    const next = new Set(state.hiddenSignalTypes);
    next.has(t) ? next.delete(t) : next.add(t);
    onChange({ ...state, hiddenSignalTypes: next });
  };

  return (
    <div className="absolute left-3 bottom-12 z-20 max-h-[70vh] w-[240px] overflow-hidden rounded-md border border-border bg-surface/95 backdrop-blur text-[11px] flex flex-col">
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="flex items-center justify-between px-3 py-2 border-b border-border text-foreground/85 hover:bg-surface-elevated"
      >
        <span className="label-eyebrow !mb-0">Legend</span>
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>

      {!collapsed && (
        <div className="flex-1 min-h-0 overflow-auto scrollbar-thin">
          <section className="px-3 py-2 border-b border-border">
            <div className="label-eyebrow !mb-1.5">Layers</div>
            <div className="space-y-0.5">
              {(["markers", "areas", "routes", "labels", "heatmap"] as LayerKey[]).map((k) => {
                const hidden = state.hiddenLayers.has(k);
                return (
                  <button
                    key={k}
                    onClick={() => toggleLayer(k)}
                    className={`flex items-center gap-2 w-full px-2 py-1 rounded hover:bg-surface-elevated ${
                      hidden ? "text-muted-foreground" : "text-foreground/90"
                    }`}
                  >
                    {hidden ? (
                      <EyeOff className="h-3 w-3 opacity-50" />
                    ) : (
                      <Eye className="h-3 w-3 text-primary" />
                    )}
                    <span>{LAYER_LABELS[k]}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {signalTypes && signalTypes.length > 0 && (
            <section className="px-3 py-2 border-b border-border">
              <div className="label-eyebrow !mb-1.5">Signal types</div>
              <div className="grid grid-cols-2 gap-0.5">
                {signalTypes.map(({ type, count }) => {
                  const hidden = state.hiddenSignalTypes.has(type);
                  return (
                    <button
                      key={type}
                      onClick={() => toggleSignalType(type)}
                      className={`flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-surface-elevated text-left ${
                        hidden ? "opacity-45" : ""
                      }`}
                      title={`${count} signal${count === 1 ? "" : "s"} · ${hidden ? "click to show" : "click to hide"}`}
                    >
                      {hidden ? (
                        <EyeOff className="h-3 w-3 opacity-50 shrink-0" />
                      ) : (
                        <Eye className="h-3 w-3 text-primary shrink-0" />
                      )}
                      <span className="truncate text-[10.5px]">{type}</span>
                      <span className="ml-auto text-[9px] font-mono text-muted-foreground shrink-0">
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section className="px-3 py-2">
            <div className="label-eyebrow !mb-1.5">
              By color · {groups.length} group{groups.length === 1 ? "" : "s"}
            </div>
            {groups.length === 0 ? (
              <div className="text-muted-foreground italic">No mapped entities yet.</div>
            ) : (
              <div className="space-y-0.5">
                {groups.map((g) => {
                  const hidden = state.hiddenColors.has(g.color);
                  return (
                    <button
                      key={g.color}
                      onClick={() => toggleColor(g.color)}
                      className={`flex items-center gap-2 w-full px-1.5 py-1 rounded hover:bg-surface-elevated text-left ${
                        hidden ? "opacity-50" : ""
                      }`}
                    >
                      <span
                        className="inline-block h-3 w-3 rounded-sm shrink-0 border border-black/30"
                        style={{ background: g.color }}
                      />
                      <span className="flex-1 min-w-0 truncate">
                        {g.entities.slice(0, 2).map((e) => e.name).join(", ")}
                        {g.entities.length > 2 && ` +${g.entities.length - 2}`}
                      </span>
                      <span className="flex items-center gap-0.5 text-muted-foreground">
                        {g.hasMarker && <span title="markers">●</span>}
                        {g.hasArea && <span title="areas">▣</span>}
                        {g.hasRoute && <span title="routes">━</span>}
                      </span>
                      <span className="text-muted-foreground font-mono">{g.entities.length}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
